/**
 * WaitingOnYouSection — top of the Catchball Inbox.
 *
 * Open questions someone else asked on any ball in this cycle that the viewer
 * is a party to (holder, creator, requester, cycle owner/admin). Questions on
 * the ball being viewed jump to its "Open questions" section; the rest link
 * to their own ball. Renders nothing when nothing is waiting.
 */

import { Link } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { buildCatchballInboxUrl } from '../../../../lib/catchballInboxUrls';
import type { LoopInquiry } from './threadLogic';
import { formatWhen } from './ThreadComposer';

export function WaitingOnYouSection({
  cycleId,
  currentLoopId,
  inquiries,
  labelForLoop,
  nameForActor,
}: {
  cycleId: string;
  currentLoopId: string;
  /** Already filtered + ordered by inquiriesAwaitingViewer. */
  inquiries: LoopInquiry[];
  labelForLoop: (loopId: string) => string;
  nameForActor: (actorId: string) => string;
}) {
  if (inquiries.length === 0) return null;

  return (
    <section className="border border-[var(--mustard)] bg-[var(--mustard-dim)]/60 px-5 py-4 space-y-2">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--text-pri)]">
        <HelpCircle className="h-4 w-4 text-[var(--mustard)]" />
        {inquiries.length === 1 ? '1 question is waiting on you' : `${inquiries.length} questions are waiting on you`}
      </h2>
      <ul className="space-y-1.5">
        {inquiries.map((q) => {
          const here = q.loopId === currentLoopId;
          const body = (
            <>
              <span className="block text-sm text-[var(--text-pri)] line-clamp-2">{q.question}</span>
              <span className="block text-xs text-[var(--text-ter)]">
                {nameForActor(q.openedBy)} · {here ? 'this ball' : labelForLoop(q.loopId)}
                {formatWhen(q.openedAt) && ` · ${formatWhen(q.openedAt)}`}
              </span>
            </>
          );
          return (
            <li key={q.id}>
              {here ? (
                <a href="#inbox-questions" className="block hover:underline">
                  {body}
                </a>
              ) : (
                <Link
                  to={`${buildCatchballInboxUrl(cycleId, q.loopId)}#inbox-questions`}
                  className="block hover:underline"
                >
                  {body}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
